import { router, usePage } from '@inertiajs/vue3'
import { computed, ref, shallowRef } from 'vue'

/**
 * RED-346 — upload / remove the appraiser's finished .docx for the quality check.
 *
 * The truth lives in the `reportQc` Inertia prop (server-gated: null when the
 * flag is off or the permission is missing). After every change the composable
 * partially reloads that prop instead of trusting the JSON echo, so what the
 * panel shows is what a page refresh would also show (the RED-235 lesson).
 *
 * The size limit comes from the same prop, so the client never promises what
 * the server refuses. Every other rejection is decided by the server and comes
 * back as a `code` the component translates (spec §5).
 *
 * @param {string|number} evaluationId
 */
export function useReportUpload(evaluationId) {
  const page = usePage()

  const upload = computed(() => page.props.reportQc?.upload ?? null)
  const maxBytes = computed(() => page.props.reportQc?.maxBytes ?? 50 * 1024 * 1024)
  const retentionDaysMax = computed(() => page.props.reportQc?.retentionDaysMax ?? null)

  const uploading = shallowRef(false)
  const reading = shallowRef(false)
  const removing = shallowRef(false)
  const loadedBytes = shallowRef(0)
  const totalBytes = shallowRef(0)
  /** @type {import('vue').Ref<{ code: string, maxMegabytes?: number|null } | null>} */
  const rejection = ref(null)

  let controller = null

  const refresh = () =>
    new Promise(resolve => {
      router.reload({ only: ['reportQc'], onFinish: resolve })
    })

  const mapError = err => {
    const status = err?.response?.status
    const body = err?.response?.data
    if (status === 422 && body?.code) {
      return { code: body.code, maxMegabytes: body.maxMegabytes ?? null }
    }
    if (status === 413) {
      return { code: 'too_large', maxMegabytes: Math.floor(maxBytes.value / (1024 * 1024)) }
    }
    if (status === 419) {
      return { code: 'csrfExpired' }
    }
    return { code: 'generic' }
  }

  /**
   * Send one file. Resolves true when the upload landed and the prop was refreshed.
   * @param {File} file
   */
  const send = async file => {
    rejection.value = null

    if (file.size > maxBytes.value) {
      rejection.value = { code: 'too_large', maxMegabytes: Math.floor(maxBytes.value / (1024 * 1024)) }
      return false
    }

    controller = new AbortController()
    uploading.value = true
    reading.value = false
    loadedBytes.value = 0
    totalBytes.value = file.size

    const formData = new FormData()
    formData.append('file', file)

    try {
      await axios.post(route('evaluation.report-upload.store', evaluationId), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: controller.signal,
        onUploadProgress: event => {
          loadedBytes.value = event.loaded
          totalBytes.value = event.total ?? file.size
          // Bytes are all in; the server is now reading the file.
          if (event.total && event.loaded >= event.total) {
            uploading.value = false
            reading.value = true
          }
        },
      })
      await refresh()
      return true
    } catch (err) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        return false
      }
      console.error('Report upload failed', { status: err?.response?.status, body: err?.response?.data, error: err })
      rejection.value = mapError(err)
      return false
    } finally {
      uploading.value = false
      reading.value = false
      controller = null
    }
  }

  const cancel = () => {
    controller?.abort()
  }

  const remove = async () => {
    rejection.value = null
    removing.value = true
    try {
      await axios.delete(route('evaluation.report-upload.destroy', evaluationId))
      await refresh()
    } catch (err) {
      console.error('Report upload removal failed', { status: err?.response?.status, error: err })
      rejection.value = mapError(err)
    } finally {
      removing.value = false
    }
  }

  const clearRejection = () => {
    rejection.value = null
  }

  return {
    upload,
    maxBytes,
    retentionDaysMax,
    uploading,
    reading,
    removing,
    loadedBytes,
    totalBytes,
    rejection,
    send,
    cancel,
    remove,
    clearRejection,
  }
}
